// FirstRunHost.tsx · v12.10.25.2 (#2106 Tushar 10/30: the checklist has Restart and Close buttons)
// FirstRunHost.tsx · v12.10.25.1 (#2106 Tushar 10/30: checklist Show me opens a message and spotlights the button; Start from step 1 walks every step)
// FirstRunHost.tsx · v12.10.25 (#2106 first-time user - Welcome card, spotlight tours, the checklist pill) · 2026-10-30 · VoiceSpectrum AI
// LOCKED prototype: Documents\Scope\Work In Progress\UI-PROTO-FIRST-RUN-T180_v1.4.html (light mode, Tushar 10/30).
// Mounted once by the Shell. It:
//  - opens the Welcome card for a person with five or fewer sign-ins who has not chosen yet ("Welcome, <first name>";
//    "<Practice>'s calls are now answered by VoiceSpectrum AI. Take two minutes to see how it works and how you handle
//    each call."; Show me around · Give me a checklist · I'll explore on my own) - never on a Support visit, never in Help;
//  - runs a guide's spotlight tour on the real screens (steps the person's rights cannot open are skipped);
//  - shows the checklist pill "Let's get started · n/N": ONE step at a time (Show me · Skip step · Hide), each step ticks
//    itself from what the person really does and the pill moves to the next step on its own (green flash);
//  - listens to the app: data-track clicks (lib/uiTrack.ts names), audio Play, the route, a few button words.
// Commands come from Help (Play guide / Checklist) and the name menu (Take the tour again / Show checklist) via sendFirstRun.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccess } from '../../lib/useAccess';
import { toast } from '../../lib/toast';
import {
  FIRST_RUN_EVENT, FIRST_RUN_MENU_EVENT, checkOf, guideByKey, rightsFrom, ticksForButtonText, ticksForPlay, ticksForRoute,
  ticksForSearch, ticksForTrack, takeParked, tourOf, useFirstRun, type FirstRunCommand, type GuideKey, type Tick,
} from '../../lib/firstRun';
import './firstrun.css';

interface Box { left: number; top: number; width: number; height: number }

function rectOf(sel: string | undefined): Box | null {
  if (!sel) return null;
  const el = document.querySelector(sel) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}
function pulse(sel: string | undefined, ms = 2800) {
  if (!sel) return;
  const el = document.querySelector(sel) as HTMLElement | null;
  if (!el) return;
  try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* old browsers */ }   // v12.10.25.1: never pulse off screen
  el.classList.add('fr-pulse');
  window.setTimeout(() => el.classList.remove('fr-pulse'), ms);
}
function menu(open: boolean) {
  try { window.dispatchEvent(new CustomEvent(FIRST_RUN_MENU_EVENT, { detail: { open } })); } catch { /* old browsers */ }
}
const pillKey = (uid: number | string | undefined) => `vs.firstRun.pill.${uid ?? 'me'}`;

export default function FirstRunHost({ helpMode }: { helpMode: boolean }) {
  const { me } = useAccess();
  const can = useMemo(() => rightsFrom(me?.access as Record<string, string[] | undefined> | undefined), [me]);
  const { data, loaded, save } = useFirstRun();
  const navigate = useNavigate();
  const location = useLocation();
  const isSupport = !!me?.isSupport;
  const uid = me?.user?.id;

  const [welcomeGone, setWelcomeGone] = useState(false);
  const [tour, setTour] = useState<{ guide: GuideKey; i: number } | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [pill, setPillState] = useState<GuideKey | null>(null);
  const [pillOpen, setPillOpen] = useState(true);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [flash, setFlash] = useState(false);

  // the pill survives a reload (per person, this browser)
  useEffect(() => {
    if (uid === undefined) return;
    try { const v = localStorage.getItem(pillKey(uid)); if (v && guideByKey(v)) setPillState(v as GuideKey); } catch { /* private window */ }
  }, [uid]);
  const setPill = useCallback((g: GuideKey | null) => {
    setPillState(g); setSkipped([]); setPillOpen(true);
    try { if (g) localStorage.setItem(pillKey(uid), g); else localStorage.removeItem(pillKey(uid)); } catch { /* private window */ }
  }, [uid]);

  // ── ticking ───────────────────────────────────────────────────────────────────────────────────────────
  const dataRef = useRef(data); dataRef.current = data;
  const tick = useCallback((list: Tick[]) => {
    const d = dataRef.current;
    if (!d || isSupport || list.length === 0) return;
    const by = new Map<string, string[]>();
    for (const [g, s] of list) if (!(d.progress[g] ?? []).includes(s)) by.set(g, [...(by.get(g) ?? []), s]);
    by.forEach((done, guide) => { void save({ guide, done }); });
  }, [isSupport, save]);

  const locRef = useRef(location); locRef.current = location;
  useEffect(() => {
    let searched = false;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (!t || typeof t.closest !== 'function' || t.closest('.fr-layer')) return;
      const tracked = t.closest('[data-track]');
      if (tracked) tick(ticksForTrack(tracked.getAttribute('data-track') ?? ''));
      const btn = t.closest('button, a, [role="menuitem"]');
      if (btn) tick(ticksForButtonText(locRef.current.pathname, locRef.current.search, (btn.textContent ?? '').slice(0, 40)));
    };
    const onPlay = () => tick(ticksForPlay(locRef.current.pathname));
    const onInput = (e: Event) => {
      const t = e.target as Element | null;
      if (!searched && t && typeof t.closest === 'function' && t.closest('.aw-search')) { searched = true; tick(ticksForSearch()); }
    };
    document.addEventListener('click', onClick, { capture: true, passive: true });
    document.addEventListener('play', onPlay, true);
    document.addEventListener('input', onInput, true);
    return () => {
      document.removeEventListener('click', onClick, { capture: true } as EventListenerOptions);
      document.removeEventListener('play', onPlay, true);
      document.removeEventListener('input', onInput, true);
    };
  }, [tick]);
  useEffect(() => { if (loaded) tick(ticksForRoute(location.pathname, location.search)); }, [location.pathname, location.search, loaded, tick]);

  // ── tours ─────────────────────────────────────────────────────────────────────────────────────────────
  const startTour = useCallback((guide: GuideKey) => {
    const g = guideByKey(guide);
    if (!g || tourOf(g, can).length === 0) { toast('info', 'Nothing in this guide for your user group'); return; }
    setWelcomeGone(true);
    setTour({ guide, i: 0 });
  }, [can]);
  const steps = useMemo(() => { const g = tour ? guideByKey(tour.guide) : undefined; return g ? tourOf(g, can) : []; }, [tour, can]);
  const step = tour ? steps[tour.i] : undefined;

  const endTour = useCallback((finished: boolean) => {
    if (!tour) return;
    const g = guideByKey(tour.guide);
    menu(false);
    setTour(null); setBox(null);
    tick([[tour.guide, 'tour']]);
    toast('info', `${g?.name ?? 'Guide'} ${finished ? 'done' : 'skipped'}`, 'Help brings every guide back any time.');
  }, [tour, tick]);

  // show the step: go to its screen, open what it needs, then find what to spotlight
  useEffect(() => {
    if (!step) return;
    let alive = true;
    if (step.route) {
      const [p, q] = step.route.split('?');
      if (locRef.current.pathname !== p || (q && !locRef.current.search.includes(q))) navigate(step.route);
    }
    const t0 = window.setTimeout(() => {
      if (!alive) return;
      if (step.prep === 'closeDrawer') { (document.querySelector('.aw-drawer-close') as HTMLElement | null)?.click(); menu(false); }
      if (step.prep === 'closeMenu') menu(false);
      if (step.prep === 'openMenu') menu(true);
      if (step.prep === 'openRow' && !document.querySelector('.aw-drawer')) (document.querySelector('[data-track="messages.row.open"]') as HTMLElement | null)?.click();
      if (step.pulse) pulse(step.pulse, 4000);
    }, 250);
    let tries = 0;
    const find = window.setInterval(() => {
      if (!alive) return;
      const r = rectOf(step.target);
      if (r || ++tries > 12) { setBox(r); }
      if (r) { const el = document.querySelector(step.target!) as HTMLElement | null; el?.scrollIntoView?.({ block: 'nearest' }); }
    }, 250);
    return () => { alive = false; window.clearTimeout(t0); window.clearInterval(find); };
  }, [step, navigate]);
  // keep the spotlight on the thing while the page settles / scrolls / resizes
  useEffect(() => {
    if (!step) return;
    const re = () => { const r = rectOf(step.target); if (r) setBox(r); };
    window.addEventListener('resize', re);
    window.addEventListener('scroll', re, true);
    return () => { window.removeEventListener('resize', re); window.removeEventListener('scroll', re, true); };
  }, [step]);

  // ── commands (Help, name menu, Welcome) ─────────────────────────────────────────────────────────────────
  const run = useCallback((cmd: FirstRunCommand) => {
    if (cmd.action === 'tour') startTour(cmd.guide);
    else if (cmd.action === 'checklist') { setTour(null); setPill(cmd.guide); }
    else if (cmd.action === 'welcome') setWelcomeGone(false);
  }, [startTour, setPill]);
  useEffect(() => {
    const on = (e: Event) => { const d = (e as CustomEvent<FirstRunCommand>).detail; if (d) run(d); };
    window.addEventListener(FIRST_RUN_EVENT, on);
    return () => window.removeEventListener(FIRST_RUN_EVENT, on);
  }, [run]);
  useEffect(() => { if (!helpMode) { const p = takeParked(); if (p) run(p); } }, [helpMode, run]);

  // ── the pill: one step at a time, moves on by itself ────────────────────────────────────────────────────
  const pillGuide = pill ? guideByKey(pill) : undefined;
  const items = useMemo(() => (pillGuide ? checkOf(pillGuide, can) : []), [pillGuide, can]);
  const doneKeys = (pill && data?.progress[pill]) || [];
  const doneCount = items.filter((c) => doneKeys.includes(c.key)).length;
  const open = items.filter((c) => !doneKeys.includes(c.key));
  // v12.10.25.1 (Tushar 10/30): "Start from step 1" walks every step in order (done ones marked ✓); null = the next step not done yet
  const [walk, setWalk] = useState<number | null>(null);
  const nextOpen = open.find((c) => !skipped.includes(c.key)) ?? open[0];
  const current = walk !== null && items[walk] ? items[walk] : nextOpen;
  const stepNo = walk !== null && items[walk] ? walk + 1 : doneCount + 1;
  const currentDone = !!current && doneKeys.includes(current.key);
  const lastCurrent = useRef<string | undefined>(undefined);
  useEffect(() => {
    const was = lastCurrent.current;
    lastCurrent.current = current?.key;
    if (was && pill && doneKeys.includes(was)) {
      setFlash(true); window.setTimeout(() => setFlash(false), 1000);
      toast('success', current ? '✓ Done - next step' : '✓ All done');
    }
  }, [current?.key]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pill && items.length > 0 && open.length === 0 && walk === null) { const t = window.setTimeout(() => setPill(null), 2400); return () => window.clearTimeout(t); }
    return undefined;
  }, [pill, items.length, open.length, setPill]);

  function showMe() {
    if (!current) return;
    const item = current;
    let wait = 450;
    if (item.route) {
      const [p, q] = item.route.split('?');
      if (location.pathname !== p || (q && !location.search.includes(q))) { navigate(item.route); wait = 900; }
    }
    // v12.10.25.1 (Tushar 10/30: "when i clicked on show me, it didn't do anything"): a step inside a message opens one first
    window.setTimeout(() => {
      if (item.inDrawer && !document.querySelector('.aw-drawer')) (document.querySelector('[data-track="messages.row.open"]') as HTMLElement | null)?.click();
      window.setTimeout(() => pulse(item.target), item.inDrawer ? 700 : 0);
    }, wait);
  }

  // ── the Welcome ─────────────────────────────────────────────────────────────────────────────────────────
  const showWelcome = loaded && !!data?.show && !isSupport && !helpMode && !welcomeGone && !tour;
  function choose(c: 'tour' | 'checklist' | 'skip') {
    setWelcomeGone(true);
    if (c === 'tour') { void save({ state: 'toured' }); startTour('fam'); }
    else if (c === 'checklist') { void save({ state: 'checklist' }); setPill('fam'); }
    else { void save({ state: 'skipped' }); toast('info', 'Nothing will open again by itself', 'Help brings the guides back any time.'); }
  }
  const practice = data?.practiceName || 'Your practice';
  const first = data?.firstName || me?.user?.firstName || '';

  // ── render ──────────────────────────────────────────────────────────────────────────────────────────────
  const tip = useTipPosition(box);
  return (
    <div className="fr-layer">
      {showWelcome && (
        <div className="fr-modalwrap" role="dialog" aria-modal="true" aria-label="Welcome">
          <div className="fr-modal">
            <h2>Welcome{first ? `, ${first}` : ''} <span aria-hidden="true">👋</span></h2>
            <p className="fr-sub">{practice}'s calls are now answered by VoiceSpectrum AI. Take two minutes to see how it works and how you handle each call.</p>
            <button type="button" className="fr-opt" onClick={() => choose('tour')}>
              <span className="fr-n" aria-hidden="true">▶</span>
              <span><b>Show me around · 2 minutes</b><span>The basics on your own screens. You can stop any time.</span></span>
            </button>
            <button type="button" className="fr-opt" onClick={() => choose('checklist')}>
              <span className="fr-n" aria-hidden="true">✓</span>
              <span><b>Give me a checklist</b><span>Learn by doing - small steps that tick themselves off as you work.</span></span>
            </button>
            <button type="button" className="fr-later" onClick={() => choose('skip')}>I'll explore on my own - Help brings this back any time</button>
          </div>
        </div>
      )}

      {tour && step && (
        <>
          <div className="fr-scrim" aria-hidden="true">
            {box ? <div className="fr-spot" style={{ left: box.left - 6, top: box.top - 6, width: box.width + 12, height: box.height + 12 }} />
                 : <div className="fr-dim" />}
          </div>
          <div className="fr-tip" role="dialog" aria-label={step.title} style={tip}>
            <small>{guideByKey(tour.guide)?.name.toUpperCase()} · {tour.i + 1} OF {steps.length}</small>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
            <div className="fr-bar">
              <div className="fr-dots">{steps.map((_, i) => <span key={i} className={i <= tour.i ? 'on' : ''} />)}</div>
              <button type="button" className="fr-skip" onClick={() => endTour(false)}>Skip</button>
              {tour.i > 0 && <button type="button" className="fr-btn" onClick={() => setTour({ ...tour, i: tour.i - 1 })}>Back</button>}
              <button type="button" className="fr-btn p" onClick={() => (tour.i >= steps.length - 1 ? endTour(true) : setTour({ ...tour, i: tour.i + 1 }))}>
                {tour.i >= steps.length - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </>
      )}

      {pillGuide && items.length > 0 && !tour && !showWelcome && (
        <div className="fr-pill">
          {pillOpen && (
            <div className={`fr-step${flash ? ' fr-flash' : ''}`} role="status">
              <small>{pillGuide.name.toUpperCase()} · {current ? `STEP ${stepNo} OF ${items.length}${currentDone ? ' · DONE ✓' : ''}` : 'ALL DONE'}</small>
              <div className="fr-prog"><i style={{ width: `${(doneCount / items.length) * 100}%` }} /></div>
              {current ? (
                <>
                  <h4>{current.label}</h4>
                  <p>{current.how}</p>
                  <div className="fr-ft">
                    <button type="button" className="fr-btn p" onClick={showMe}>Show me</button>
                    {walk !== null
                      ? <button type="button" className="fr-btn" onClick={() => setWalk((w) => (w !== null && w + 1 < items.length ? w + 1 : null))}>{walk + 1 < items.length ? 'Next step' : 'Finish'}</button>
                      : open.length > 1 && <button type="button" className="fr-btn" onClick={() => setSkipped((s) => [...s.filter((k) => k !== current.key), current.key])}>Skip step</button>}
                    {walk === null && stepNo > 1 && <button type="button" className="fr-btn" onClick={() => setWalk(0)}>Restart</button>}
                    <button type="button" className="fr-btn" onClick={() => { setWalk(null); setPill(null); toast('info', 'Checklist closed', 'Help › any guide › Checklist brings it back.'); }}>Close</button>
                  </div>
                </>
              ) : (
                <><h4>All done</h4><p className="fr-ok">Every step is ticked. The pill hides itself - Help brings it back.</p></>
              )}
            </div>
          )}
          <button type="button" className="fr-pillbtn" onClick={() => setPillOpen((v) => !v)}>
            {current ? `${pill === 'fam' ? "Let's get started" : pillGuide.name} · ${stepNo}/${items.length}` : `${pillGuide.name} · all done ✓`}
          </button>
        </div>
      )}
    </div>
  );
}

/** The tip sits beside the spotlight (right, else left, else below); no spotlight = centred. */
function useTipPosition(box: Box | null): CSSProperties {
  const [vw, setVw] = useState(() => window.innerWidth);
  const [vh, setVh] = useState(() => window.innerHeight);
  useLayoutEffect(() => {
    const on = () => { setVw(window.innerWidth); setVh(window.innerHeight); };
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const W = Math.min(340, vw - 24), H = 200;
  if (!box) return { left: Math.max(12, (vw - W) / 2), top: Math.max(12, (vh - H) / 2), width: W };
  let left = box.left + box.width + 16;
  let top = box.top;
  if (left + W > vw - 12) left = box.left - W - 16;
  if (left < 12) { left = Math.min(Math.max(12, box.left), vw - W - 12); top = box.top + box.height + 14; if (top + H > vh) top = box.top - H - 14; }
  top = Math.min(Math.max(12, top), vh - H - 12);
  return { left, top, width: W };
}
